<div class="board">
  <div class="head">
    <div>
      <h2>Events</h2>
      <p class="sub">
        Every space, who runs it, who is in it, and whether its lobby is a
        conversation or a noticeboard. Lobby numbers are counts and timestamps
        only — nothing anyone said is read.
      </p>
    </div>
    <div class="head-actions">
      <app-range-switch [days]="days" (daysChange)="setRange($event)"></app-range-switch>
      <button mat-stroked-button (click)="load()" [disabled]="loading">Refresh</button>
    </div>
  </div>

  <div class="notice bad" *ngIf="error">{{ error }}</div>

  <ng-container *ngIf="overview as o">
    <div class="stats">
      <div class="stat">
        <div class="n">{{ o.totals.events }}</div>
        <div class="l">Spaces</div>
        <div class="hint">{{ o.totals.silentLobbies }} never used their lobby</div>
      </div>
      <div class="stat" [class.good]="o.totals.talkingLobbies > 0">
        <div class="n">{{ o.totals.talkingLobbies }}<small>/ {{ o.totals.activeLobbies }}</small></div>
        <div class="l">Talking / active<app-info-hint label="Talking / active"
          text="Active: lobbies with at least one message in range. Talking: those where the speaker changed hands 4 or more times — the same bar the chat engagement board uses for a conversation."
        ></app-info-hint></div>
        <div class="hint">lobbies in the last {{ o.range.days }} days</div>
      </div>
      <div class="stat">
        <div class="n">{{ o.totals.members }}</div>
        <div class="l">Members<app-info-hint label="Members"
          text="Distinct people who belong to at least one space. Someone in three spaces counts once here, and once per space in the table."
        ></app-info-hint></div>
        <div class="hint">+{{ o.totals.joinedInWindow }} joins in range</div>
      </div>
      <div class="stat">
        <div class="n">{{ o.totals.lobbyMessagesInWindow }}</div>
        <div class="l">Lobby messages</div>
        <div class="hint">{{ o.totals.lobbyMessagesAllTime }} all time</div>
      </div>
      <div class="stat">
        <div class="n">{{ o.totals.peerChatsInWindow }}<small>/ {{ o.totals.peerChatsAmongMembers }}</small></div>
        <div class="l">Member-to-member chats<app-info-hint label="Member-to-member chats"
          text="One-to-one chats where both people belong to the same space — the connections a lobby is supposed to lead to. In range / all time. A chat between people who share several spaces counts for each space."
        ></app-info-hint></div>
        <div class="hint">peer chats among space members</div>
      </div>
      <div class="stat" [class.warn]="o.totals.pendingRequests > 0">
        <div class="n">{{ o.totals.pendingRequests }}</div>
        <div class="l">Waiting to join</div>
        <div class="hint">registration requests nobody answered</div>
      </div>
    </div>

    <div class="filters">
      <button [class.on]="filter === 'all'" (click)="filter = 'all'">All ({{ visible.length }})</button>
      <button *ngFor="let s of states" [class.on]="filter === s" (click)="filter = s" [title]="stateHint[s]">
        {{ stateLabel[s] }} ({{ countIn(s) }})
      </button>
      <label
        class="toggle"
        [title]="'Spaces nobody has claimed yet: no organizer, no members, nothing happening in them.'"
      >
        <input type="checkbox" [checked]="showUnclaimed" (change)="showUnclaimed = !showUnclaimed" />
        Show unclaimed ({{ unclaimedCount }})
      </label>
      <span class="spacer"></span>
      <span class="label">Sort</span>
      <button [class.on]="sort === 'active'" (click)="sort = 'active'">Most active</button>
      <button [class.on]="sort === 'members'" (click)="sort = 'members'">Most members</button>
      <button [class.on]="sort === 'waiting'" (click)="sort = 'waiting'">Waiting</button>
      <button [class.on]="sort === 'newest'" (click)="sort = 'newest'">Newest</button>
    </div>
  </ng-container>

  <div class="loading" *ngIf="loading"><mat-spinner diameter="28"></mat-spinner></div>

  <table *ngIf="!loading && rows.length">
    <thead>
      <tr>
        <th>Space</th>
        <th>Organizer</th>
        <th class="num">Members<app-info-hint label="Members"
          text="People who joined the space, and how many of them joined in range."
        ></app-info-hint></th>
        <th class="num">Lobby<app-info-hint label="Lobby"
          text="Messages posted in the lobby in range, then all time. Reactions are not counted as messages."
        ></app-info-hint></th>
        <th class="num">Voices<app-info-hint label="Voices"
          text="People who posted at least once in range, and the share of all members who have ever posted. A room where 3 of 60 members speak is a broadcast, whatever the message count."
        ></app-info-hint></th>
        <th>State<app-info-hint label="State"
          text="Silent: never used. Quiet: nothing in range. One voice: a single poster. Some replies: others answered but the speaker changed fewer than 4 times. Conversation: 4 or more changes of speaker."
        ></app-info-hint></th>
        <th class="num">Organizer share<app-info-hint label="Organizer share"
          text="Share of lobby messages sent by the owner or a moderator, in range (all time when the range is empty). Near 100% means the room only hears from its organizer."
        ></app-info-hint></th>
        <th class="num">1:1 chats<app-info-hint label="1:1 chats"
          text="Peer chats between two members of this space: all time, and how many got a reply."
        ></app-info-hint></th>
        <th class="num">Waiting</th>
        <th>Last activity</th>
      </tr>
    </thead>
    <tbody>
      <tr *ngFor="let e of rows" [attr.data-state]="e.lobby.state">
        <td>
          <app-event-link [eventId]="e.id" [title]="e.title"></app-event-link>
          <div class="meta">
            {{ e.tag }} · {{ e.type }}{{ e.isPrivate ? ' · private' : '' }}
            {{ e.lobbyMode ? '· ' + e.lobbyMode : '' }}
          </div>
          <div class="meta">
            {{ when(e) }}
            <span *ngIf="timing(e) as t"> · {{ t }}</span>
            <span *ngIf="e.posts"> · {{ e.posts }} feed posts</span>
          </div>
        </td>
        <td>
          <ng-container *ngIf="e.owner; else noOwner">
            <app-person-link [uid]="e.owner.uid" [name]="e.owner.name" (failed)="error = $event"></app-person-link>
            <div class="meta">{{ e.owner.companyName || e.owner.email || '' }}</div>
            <div class="meta" *ngIf="e.moderatorCount">+{{ e.moderatorCount }} moderator{{ e.moderatorCount === 1 ? '' : 's' }}</div>
          </ng-container>
          <ng-template #noOwner>
            <span class="meta">unclaimed</span>
          </ng-template>
        </td>
        <td class="num">
          {{ e.memberCount }}
          <span class="meta" *ngIf="e.joinedSince">+{{ e.joinedSince }} in range</span>
        </td>
        <td class="num">
          {{ e.lobby.window.messages }}
          <span class="meta">{{ e.lobby.allTime.messages }} all time</span>
        </td>
        <td class="num">
          {{ e.lobby.window.voices }}
          <span class="meta">{{ percent(e.participationRate) }} ever posted</span>
        </td>
        <td>
          <span class="pill" [attr.data-state]="e.lobby.state" [title]="stateHint[e.lobby.state]">
            {{ stateLabel[e.lobby.state] }}
          </span>
        </td>
        <td class="num">{{ organizerShare(e) === null ? '—' : organizerShare(e) + '%' }}</td>
        <td class="num">
          <button
            *ngIf="e.peerChats.total; else noChats"
            type="button"
            class="drill"
            title="See who chatted with whom — names and times only"
            (click)="openPeerChats(e, $event)"
          >
            {{ e.peerChats.total }}
            <span class="meta">{{ e.peerChats.replied }} replied</span>
          </button>
          <ng-template #noChats>0</ng-template>
        </td>
        <td class="num">
          <span [class.hot]="e.pendingRequests > 0">{{ e.pendingRequests || '—' }}</span>
        </td>
        <td class="meta">{{ ago(e.lobby.allTime.lastMessageAt) }}</td>
      </tr>
    </tbody>
  </table>

  <p class="empty" *ngIf="!loading && !rows.length && overview">
    No spaces match this filter.
    <span *ngIf="!showUnclaimed && unclaimedCount">
      {{ unclaimedCount }} unclaimed {{ unclaimedCount === 1 ? 'space is' : 'spaces are' }} hidden —
      tick "Show unclaimed" to include them.
    </span>
  </p>
</div>
